import React, { useEffect, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { selectTransfersPageContext } from 'selectors/transfersSelectors';
import {
  getPendingTransferCards,
  getPendingTransferCardsReset,
  cancelPendingTransferBatchRequest,
  updatePendingTransferBatchRequest,
  pendingTransfersDownloadRequest,
} from '../../actions/cardActions';
import { DataTable } from '@ampol/design-system';
import { DATE_FORMAT } from 'utilities/constants';

import './transfers.scss';
import PageHeader from '../../components/layout/page-header/page-header';
import ContentControls from '../../components/content-controls/content-controls';
import SearchInput from '../../components/search-input/search-input';
import Paginator from '../../components/pagination/pagination';
import ActionMenu from '../../components/action-menu/action-menu';
import ActionModal from '../../components/_global/action-modal/action-modal';
import DeleteTransferCardForm from '../../components/delete-transfer-card-form/delete-transfer-card-form';
import dayjs from 'dayjs';
import TransferCardsForm from 'features/cards/components/transfer-cards-form/transfer-cards-form';
import isEmpty from 'utilities/isEmpty';
import Validation from 'constants/validation-messages';
import { trackCustomEvent } from 'utilities/app-insights';
import { useTransferSelection } from 'hooks/useTransferSelection';

type PendingTransferCard = {
  cardNumber: string;
  sourceContractIdentifier: string;
  targetContractIdentifier: string;
  activeFrom: string;
};

const Transfers: React.FC = () => {
  const dispatch = useDispatch();

  const actionMenuItems = [
    { displayText: 'Edit', route: '/editpendingtransfer' },
    { displayText: 'Delete', route: '/deletependingtransfer' },
  ];

  const {
    allAccounts,
    pendingTransferCards,
    pendingTransferCardsSize,
    errors,
    assignedGrandfatherClientNumber,
    assignedAccountNumber,
    cancelPendingTransferBatchLoaded,
    updatePendingTransferBatchLoaded,
    canReadWrite,
    isDataTableLoading,
    loggedInUser,
  } = useSelector(selectTransfersPageContext);

  const {
    selection,
    showActionMenu,
    selectAll,
    resetSelection: clearSelection,
    triggerSelectAll: applyTriggerSelectAll,
    handleSelectionChange: applyHandleSelectionChange,
    getNumberOfSelectedRecords,
  } = useTransferSelection({ pendingTransferCards });

  const [showEditTransferCard, setShowEditTransferCard] = useState(false);
  const [showDeleteTransferCard, setShowDeleteTransferCard] = useState(false);
  const [offsetLeftPosition, setOffsetLeftPosition] = useState(0);

  const isMounted = useRef(false);

  const [selectedAccountNumber, setSelectedAccountNumber] = useState<string | null>(null);

  const defaultFilterValues: {
    pageSize: number;
    pageNumber: number;
    sortTypeSelected: string;
    sortDirection: boolean;
    searchTerm: string | null;
  } = {
    pageSize: 10,
    pageNumber: 1,
    sortTypeSelected: 'CardNumber',
    sortDirection: false,
    searchTerm: null,
  };

  const [filterValues, setFilterValues] = useState(defaultFilterValues);

  useEffect(() => {
    if (assignedGrandfatherClientNumber && isMounted.current) {
      setSelectedAccountNumber(null);
    }

    isMounted.current = true;
  }, [assignedGrandfatherClientNumber]);

  useEffect(() => {
    if (isMounted.current) {
      setFilterValues({
        ...defaultFilterValues,
      });
      resetSelection();
    }

    dispatch(getPendingTransferCardsReset());

    isMounted.current = true;
  }, [assignedAccountNumber, selectedAccountNumber]);

  useEffect(() => {
    dispatchGetPendingTransferCards();
  }, [filterValues]);

  useEffect(() => {
    if (updatePendingTransferBatchLoaded || cancelPendingTransferBatchLoaded) {
      dispatchGetPendingTransferCards();
    }
  }, [updatePendingTransferBatchLoaded, cancelPendingTransferBatchLoaded]);

  useEffect(() => {
    resetSelection();
  }, [pendingTransferCards]);

  const resetSelection = () => {
    clearSelection();
    closeActionMenuModals();
  };

  const closeActionMenuModals = () => {
    setShowDeleteTransferCard(false);
    setShowEditTransferCard(false);
  };

  const triggerSelectAll = (isSelectAll?: boolean) => {
    closeActionMenuModals();
    applyTriggerSelectAll(isSelectAll);
  };

  const handleSelectionChange = (item: PendingTransferCard) => {
    closeActionMenuModals();
    applyHandleSelectionChange(item);
  };

  const handleActionMenuClick = (route: string, offsetLeftPosition: number) => {
    closeActionMenuModals();

    if (route === '/editpendingtransfer') {
      setShowEditTransferCard(!showEditTransferCard);
      trackCustomEvent(
        'Click Edit Transfer Card',
        loggedInUser,
        assignedAccountNumber,
        'Clicked to toggle action menu item on pending transfers list page.'
      );
    } else if (route === '/deletependingtransfer') {
      setShowDeleteTransferCard(!showDeleteTransferCard);
      trackCustomEvent(
        'Click Delete Transfer Card',
        loggedInUser,
        assignedAccountNumber,
        'Clicked to toggle action menu item on pending transfers list page.'
      );
    }

    setOffsetLeftPosition(offsetLeftPosition - 1);
  };

  const sortTransfers = (column: string, isAsc: boolean) => {
    setFilterValues({
      ...filterValues,
      sortTypeSelected: column,
      sortDirection: isAsc,
      pageNumber: 1,
    });
  };

  const dispatchGetPendingTransferCards = () => {
    if (selectedAccountNumber) {
      dispatch(
        getPendingTransferCards(
          filterValues.pageSize,
          filterValues.pageNumber,
          filterValues.sortTypeSelected,
          filterValues.sortDirection,
          filterValues.searchTerm,
          selectedAccountNumber,
          assignedGrandfatherClientNumber
        )
      );
    }
  };

  const searchTransfers = (query: string) => {
    window.dataLayer.push({
      event: 'Card_pending_transfers_search',
    });
    trackCustomEvent(
      'Search Pending Transfers List',
      loggedInUser,
      assignedAccountNumber,
      'Searched Pending Transfers related to a specific account.',
      { selectedAccount: selectedAccountNumber }
    );

    let searchValue: string | null = query.toLowerCase();
    resetSelection();
    closeActionMenuModals();
    setFilterValues({
      ...filterValues,
      pageNumber: 1,
      searchTerm: searchValue,
    });
  };

  const handlePageValue = (pageNumber: number) => {
    closeActionMenuModals();
    setFilterValues({
      ...filterValues,
      pageNumber,
    });

    trackCustomEvent(
      'Change Page Number',
      loggedInUser,
      assignedAccountNumber,
      'Clicked to change page of transfers data.'
    );
  };

  const downloadPendingTransfers = () => {
    dispatch(
      pendingTransfersDownloadRequest(
        assignedGrandfatherClientNumber,
        selectedAccountNumber,
        300,
        0,
        filterValues.sortTypeSelected,
        filterValues.sortDirection ? 'ASC' : 'DESC',
        filterValues.searchTerm
      )
    );
  };

  const handleEditTransferCards = (
    targetAccountNumber: string,
    activeDate: string,
    cardNumbers: string[],
    targetGrandParentAccountNumber: string
  ) => {
    const cardTransfers = pendingTransferCards;
    const transferSources: Record<string, string[]> = {};

    cardNumbers.forEach((cardNumber: string) => {
      const pendingTransfer = cardTransfers.find((x: PendingTransferCard) => x.cardNumber === cardNumber);
      if (!(pendingTransfer.sourceContractIdentifier in transferSources)) {
        transferSources[pendingTransfer.sourceContractIdentifier] = [];
      }

      transferSources[pendingTransfer.sourceContractIdentifier].push(cardNumber);
    });

    dispatch(
      updatePendingTransferBatchRequest(
        targetAccountNumber,
        activeDate,
        transferSources,
        assignedGrandfatherClientNumber,
        targetGrandParentAccountNumber
      )
    );

    setShowEditTransferCard(false);

    trackCustomEvent(
      'Submit Edit Transfer Card',
      loggedInUser,
      assignedAccountNumber,
      'Clicked Submit button to submit Edit Card(s) Transfer.',
      { selectedCard: cardNumbers }
    );
  };

  const handleDeleteTransferCards = (cardNumbers: string[]) => {
    const cardTransfers = pendingTransferCards;
    const transferTargets: Record<string, string[]> = {};

    cardNumbers.forEach((cardNumber: string) => {
      const pendingTransfer = cardTransfers.find((x: PendingTransferCard) => x.cardNumber === cardNumber);
      if (!(pendingTransfer.targetContractIdentifier in transferTargets)) {
        transferTargets[pendingTransfer.targetContractIdentifier] = [];
      }

      transferTargets[pendingTransfer.targetContractIdentifier].push(cardNumber);
    });

    dispatch(cancelPendingTransferBatchRequest(transferTargets, assignedGrandfatherClientNumber));

    trackCustomEvent(
      'Submit Delete Transfer Card',
      loggedInUser,
      assignedAccountNumber,
      'Clicked Yes button to submit Delete Card(s) Transfer.',
      { selectedCard: cardNumbers }
    );

    setShowDeleteTransferCard(false);
  };

  const getColumns = () => {
    // Disables sort if the account number is empty or null.
    const disableSort = isEmpty(selectedAccountNumber);

    return [
      {
        header: 'Card Number',
        accessorKey: 'cardNumber',
        id: 'CardNumber',
        defaultSorted: 'desc',
        disableSort,
      },
      {
        header: 'Source Account',
        accessorKey: 'sourceContractIdentifier',
        id: 'SourceContractIdentifier',
        disableSort,
      },
      {
        header: 'Target Account',
        accessorKey: 'targetContractIdentifier',
        id: 'TargetContractIdentifier',
        disableSort,
      },
      {
        header: 'Transfer Date',
        accessorFn: (rowData: PendingTransferCard) => dayjs(rowData.activeFrom).format(DATE_FORMAT),
        id: 'ActiveFrom',
        disableSort,
      },
    ];
  };

  return (
    <div>
      <div className='header-search-menu'>
        <PageHeader
          showAccountSelector={true}
          accounts={allAccounts}
          onAccountSelected={setSelectedAccountNumber}
          selectedAccountNumber={selectedAccountNumber}
        />
        <SearchInput
          onSearch={searchTransfers}
          className={'sort-filter-area__search'}
          defaultValue={filterValues?.searchTerm ?? undefined}
          searchSchema={undefined}
        />
      </div>
      <div className='sticky-action-menu'>
        <ContentControls
          showPageControls={true}
          showSearch={false}
          handleSearch={searchTransfers}
          handleDownload={downloadPendingTransfers}
          selectControl={canReadWrite}
          isSelected={selectAll}
          triggerSelectAll={triggerSelectAll}
          selectedItemCount={getNumberOfSelectedRecords()}
          totalRecords={pendingTransferCardsSize || 0}
          pageNumber={filterValues?.pageNumber}
          itemCount={pendingTransferCardsSize}
          onPageChange={handlePageValue}
          defaultSearchValue={filterValues?.searchTerm ?? undefined}
        />
        {showActionMenu && !isDataTableLoading && (
          <ActionMenu items={actionMenuItems} clickHandler={handleActionMenuClick} />
        )}
      </div>
      <div className='transfer-card'>
        {showEditTransferCard && (
          <ActionModal title={'Edit Transfer'} leftPosition={offsetLeftPosition} onClose={closeActionMenuModals}>
            <TransferCardsForm
              cardSelection={selection}
              closeForm={closeActionMenuModals}
              onEditTransferSubmit={handleEditTransferCards}
              isEdit
            />
          </ActionModal>
        )}
      </div>
      <div className='delete-transfer-card'>
        {showDeleteTransferCard && (
          <ActionModal title={'Delete Transfer'} leftPosition={offsetLeftPosition} onClose={closeActionMenuModals}>
            <DeleteTransferCardForm
              closeForm={closeActionMenuModals}
              onSubmit={handleDeleteTransferCards}
              cardSelection={selection}
            />
          </ActionModal>
        )}
      </div>
      <div className='transfer-table'>
        <DataTable
          caption='Card transfer list'
          columns={getColumns() as any}
          data={pendingTransferCards}
          isLoading={isDataTableLoading}
          tableVariant={'list'}
          onColumnSorted={sortTransfers}
          isSelectable={canReadWrite}
          onSelect={handleSelectionChange}
          selectAll={selectAll}
          noDataMessage={
            errors
              ? Validation.CONTENT_ERROR
              : selectedAccountNumber
                ? Validation.NO_CONTENT
                : Validation.SELECT_PENDING_TRANSFERS_ACCOUNT
          }
          rowSelectedFn={(item) => selection && selection[item.cardNumber]?.isSelected}
          rowIdFn={(row) => row.cardNumber}
        />
      </div>

      <div className='mobile-paginator'>
        <Paginator
          pageNumber={filterValues?.pageNumber}
          itemCount={pendingTransferCardsSize}
          onPageChange={handlePageValue}
          name={'transfers'}
        />
      </div>
    </div>
  );
};

export default Transfers;
